import React, { useState, useEffect, useRef, memo } from "react";

// Utility to POST chat history on page unload
function useSendChatHistoryOnUnload(chatMessages: any[]) {
  const chatRef = useRef(chatMessages);
  useEffect(() => {
    chatRef.current = chatMessages;
  }, [chatMessages]);
  useEffect(() => {
    function handleUnload() {
      if (chatRef.current?.length > 0) {
        fetch(
          `${import.meta.env.VITE_CC_API_URL}/v1/tools/cora/public/chat/history`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ chat: chatRef.current, source: window.location.href }),
          }
        );
      }
    }
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);
}

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";
import { X } from "lucide-react";

type ChatMessage = {
  id: string;
  sender: "user" | "coraa";
  text: string;
  timestamp: Date;
  isHtml?: boolean;
};

interface Reference {
  id: string;
  idName: string;
  description: string;
}

interface ChatApiResponse {
  data: {
    role: "assistant";
    content: string;
    references: Reference[];
    refusal: string | null;
    annotations: any[];
  };
  message: string;
}

const PROMPTS = [
  "What can we do in Mumbai for World Environment Day this June?",
  "Suggest activities for World Yoga Day across 5 cities",
  "Find June volunteering opportunities aligned with SDG 13 – Climate Action",
  "What are top companies planning for International Yoga Day?",
  "Suggest volunteering opportunities for World Day Against Child Labour (June 12)",
  "Recommend low-cost DIY kits for World Plastic Bag Free Day",
  "Suggest in-office activities for a 100-volunteer team in Bangalore",
  "What volunteering opportunities can we plan for World Youth Skills Day (July 15)?",
  "List volunteering events planned for Independence Day",
  "Suggest hybrid volunteering opportunities for remote teams",
  "Find city-based events I can scale across Chennai, Pune, and Kolkata",
  "What are Tata Motors and Infosys doing for employee volunteering?",
  "Suggest 30-minute engagement ideas for office breakout rooms",
  "Show me events that align with SDG 5 – Gender Equality in India",
  "Recommend a volunteering opportunity for the International Day of the Girl Child",
  "What’s the cost impact of a Plogathon in urban communities?",
  "Which nonprofits in India host the most inclusive Volunteer opportunities?",
  "Find activities that reduce single-use plastic across offices",
  "What are examples of Amazon’s Global Month of Volunteering?",
  "List top volunteering formats used by BFSI companies in India",
  "Suggest a ₹10,000 budget volunteering activities for a city-based CSR drive",
  "What volunteering activities can I do with 25 employees across 3 cities?",
  "Share success stories from similar corporate volunteering drives",
  "Suggest a 1-hour Volunteer Engagement I can run in our cafeteria",
  "Show options for SDG 4 – Quality Education events this month",
  "Recommend events that work for both family and employee participation",
  "List pre-designed volunteering opportunities available for June-July",
  "Find weekend-only events in Delhi NCR during GIVF",
  "Suggest coastal clean-up events in Chennai and Vizag",
  "What themes are trending in HR-led volunteering this quarter?",
  "How can we benchmark volunteer hours with peers in India?",
  "Suggest multilingual volunteering formats for diverse teams",
  "List nonprofits that provide measurable impact reports in real-time",
  "What volunteering activities are ideal for monsoon season?",
  "Show ideas for environment-themed volunteering across campuses",
  "Suggest inclusive ideas for Disability Awareness Day (Dec 3)",
  "Which SDG-focused kits are most scalable for remote teams?",
  "Show how CORAA can automate post-event storytelling for my team",
  "Show me under INR 700 per volunteer activities in Bangalore for June",
  "List Earth Week volunteering opportunities under INR 500 per head",
  "What can we do for World Blood Donor Day in Hyderabad?",
  "Suggest tree plantation drives that include post-event tracking",
  "Show accessible volunteering options for employees with disabilities",
  "Find climate-action aligned events for SDG 13",
  "Suggest financial literacy kits for government school children",
  "What are some high-impact DIY kits for office teams?",
  "List activities around menstrual hygiene awareness",
  "Find volunteering opportunities with elderly beneficiaries",
  "Suggest NGO events that include impact storytelling support",
  "What can we do for World Youth Skills Day in Tier 2 cities?",
  "Show volunteering opportunities related to gender equality (SDG 5)",
  "Find inclusive volunteering opportunities for LGBTQ+ employees",
  "Which volunteering opportunities offer 80G certificates and CSR-1 compliance?",
  "List plastic-reduction volunteering opportunities for office campuses",
  "Find onboarding-friendly kits under INR 300",
  "Suggest events for World Nature Conservation Day",
  "What did Tata Motors do for volunteering in 2023?",
  "Show corporate volunteering opportunities during Amazon's Global Month of Volunteering",
  "Suggest hybrid volunteering opportunities for employees in Mumbai + Pune",
  "Find short-format (1-hr) in-office events with measurable outputs",
  "What volunteering opportunities work best during Q2 for BFSI sector?",
  "Suggest events that align with employee mental health themes",
  "List disaster-relief focused volunteering opportunities",
  "Find NGO-led volunteering opportunities with multilingual beneficiary interaction",
  "Suggest post-event feedback formats used by top companies",
  "Show events with CO2 reduction as an output metric",
  "What are trending volunteering opportunities for Independence Day 2025?",
  "Find World Literacy Day aligned school interventions",
  "Suggest World Cleanup Day events for office campuses",
  "What volunteering opportunities are aligned with National Volunteering Month (India)?",
  "Suggest SDG-aligned content creation kits",
  "Find school supply kit drives under ₹500/employee",
  "Suggest mobile-friendly volunteering opportunities for field employees",
  "Show activities that support women artisans or self-help groups",
  "Find environmental volunteering opportunities involving coastal cleanup",
  "List activities where employees build something hands-on",
  "Suggest events with high visual storytelling potential",
];

interface CoraaAIDialogProps {
  open: boolean;
  onClose: () => void;
  initialInput?: string;
}

const HtmlIframe: React.FC<{ content: string }> = memo(({ content }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(300); // Default height

  const processHtmlContent = (content: string): string => {
    const htmlCodeBlockRegex = /```html([\s\S]*?)```/i;
    const match = content.match(htmlCodeBlockRegex);

    if (match && match[1]) {
      return match[1].trim();
    }

    return content;
  };

  const processedContent = processHtmlContent(content);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            html, body {
              margin: 0;
              padding: 0;
              font-family: system-ui, -apple-system, sans-serif;
              color: #333;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box;
              overflow-x: hidden;
            }
            body {
              padding: 20px;
              margin: 0 auto;
              max-width: 100%;
            }
            canvas, iframe, img, video {
              width: 100% !important;
              max-width: 100% !important;
              height: auto !important;
              display: block;
              margin: 0 auto;
            }
            .chart-container {
              width: 100% !important;
              position: relative;
              margin: 0 auto;
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
        </head>
        <body>
          ${processedContent}
        </body>
      </html>
    `;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();

      const resizeObserver = new ResizeObserver(() => {
        if (doc.body) {
          const newHeight = doc.body.scrollHeight;
          setHeight(newHeight + 20); // Add some padding
        }
      });

      if (doc.body) {
        resizeObserver.observe(doc.body);
      }

      return () => {
        if (doc.body) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [processedContent]);

  return (
    <div className="w-full overflow-x-auto">
      <iframe
        ref={iframeRef}
        title="HTML Content"
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: "100%",
          minWidth: "100%",
          height: `${height}px`,
          border: "none",
          borderRadius: "4px",
          backgroundColor: "#fff",
        }}
      />
    </div>
  );
});

HtmlIframe.displayName = 'HtmlIframe';

const CoraaAIDialog: React.FC<CoraaAIDialogProps> = ({
  open,
  onClose,
  initialInput,
}) => {
  const [chatUserInput, setChatUserInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [sessionReferences, setSessionReferences] = useState<Map<string, Reference>>(new Map());

  useSendChatHistoryOnUnload(chatMessages);

  useEffect(() => {
    if (open) {
      setChatUserInput(initialInput ?? "");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSessionReferences(new Map());
    }
  }, [open, initialInput]);
  const [chatIsLoading, setChatIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let animationFrame: number;
    let start: number;

    function scrollStep(timestamp: number) {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      if (scrollRef.current) {
        scrollRef.current.scrollLeft =
          (elapsed * 0.03) % scrollRef.current.scrollWidth;
      }
      animationFrame = requestAnimationFrame(scrollStep);
    }

    animationFrame = requestAnimationFrame(scrollStep);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatIsLoading]);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const containsHtml = (content: string): boolean => {
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    const htmlCodeBlockRegex = /```html[\s\S]*?```/i;

    return htmlRegex.test(content) || htmlCodeBlockRegex.test(content);
  };

  const processHtmlContent = (content: string): string => {
    const htmlCodeBlockRegex = /```html([\s\S]*?)```/i;
    const match = content.match(htmlCodeBlockRegex);

    if (match && match[1]) {
      return match[1].trim();
    }

    return content;
  };

  const renderMessageContent = (content: string) => {
    if (containsHtml(content)) {
      return <HtmlIframe content={content} />;
    }

    return (
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, ...props }) => (
              <a
                {...props}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 underline hover:text-blue-200"
              />
            ),
            img: ({ node, ...props }) => (
              <div className="image-thumbnail-container cursor-pointer my-2">
                <img
                  {...props}
                  alt={props.alt || "Image"}
                  onClick={() => setSelectedImage(props.src || null)}
                  className="max-w-[150px] max-h-[150px] object-cover rounded-md hover:opacity-90 transition-opacity border border-gray-200 shadow-sm"
                  style={{
                    width: "150px",
                    height: "150px",
                    objectFit: "cover",
                  }}
                />
              </div>
            ),
            code: ({ node, inline, className, children, ...props }: {
              node?: any;
              inline?: boolean;
              className?: string;
              children: React.ReactNode;
              [key: string]: any;
            }) => {
              const match = /language-(\w+)/.exec(className || "");
              return !inline && match ? (
                <div className="bg-black/30 p-3 rounded my-2 overflow-x-auto">
                  <code className={className} {...props}>
                    {children}
                  </code>
                </div>
              ) : (
                <code className="bg-black/20 px-1 rounded text-sm" {...props}>
                  {children}
                </code>
              );
            },
            p: ({ children }) => <p className="mb-2 text-white">{children}</p>,
            h1: ({ children }) => (
              <h1 className="text-xl font-bold mb-2 text-white">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-lg font-bold mb-2 text-white">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-md font-bold mb-1 text-white">{children}</h3>
            ),
            ul: ({ children }) => (
              <ul className="list-disc pl-5 mb-2 text-white">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal pl-5 mb-2 text-white">{children}</ol>
            ),
            li: ({ children }) => <li className="mb-1 text-white">{children}</li>,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  const handleSendMessage = async (inputText?: string) => {
    const text = inputText?.trim() || chatUserInput.trim();
    if (!text) return;

    const userMessage = {
      id: Date.now().toString(),
      sender: "user" as const,
      text: text,
      timestamp: new Date(),
    };

    const newMessages = [...chatMessages, userMessage];
    setChatMessages(newMessages);
    setChatUserInput("");
    setChatIsLoading(true);

    try {
      const apiMessages = newMessages.map((msg) => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: msg.text,
      }));

      const referencesArray = Array.from(sessionReferences.values());

      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/tools/cora/public/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            messages: apiMessages,
            references: referencesArray 
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const data: ChatApiResponse = await response.json();

      if (data.data?.references && data.data.references.length > 0) {
        setSessionReferences(prevRefs => {
          const newRefs = new Map(prevRefs);
          data.data.references.forEach(ref => {
            newRefs.set(ref.id, ref);
          });
          return newRefs;
        });
      }

      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      if (data.data?.refusal) {
        const refusalContent = data.data.refusal;
        const isHtml = containsHtml(refusalContent);

        const refusalMessage = {
          id: Date.now().toString(),
          sender: "coraa" as const,
          text: refusalContent,
          timestamp: new Date(),
          isHtml: isHtml,
        };

        setChatMessages([...newMessages, refusalMessage]);
      } else {
        const content =
          data.data?.content ||
          "I'm sorry, I couldn't process your request. Please try again.";
        const isHtml = containsHtml(content);

        const assistantMessage = {
          id: Date.now().toString(),
          sender: "coraa" as const,
          text: content,
          timestamp: new Date(),
          isHtml: isHtml,
        };

        setChatMessages([...newMessages, assistantMessage]);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now().toString(),
        sender: "coraa" as const,
        text: "I'm sorry, there was an error processing your request. Please try again later.",
        timestamp: new Date(),
        isHtml: false,
      };

      setChatMessages([...newMessages, errorMessage]);
    } finally {
      setChatIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && initialInput) {
      handleSendMessage(initialInput);
    }
  }, [open, initialInput]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="relative w-[90vw] h-[80vh] bg-white/20 backdrop-blur-2xl rounded-3xl shadow-2xl flex flex-col border border-white/10">
          <button
            onClick={onClose}
            className="absolute top-6 right-8 text-white text-2xl font-bold hover:text-pink-400 focus:outline-none z-10"
            aria-label="Close dialog"
          >
            ×
          </button>
          <div className="pt-4 pb-1 px-4 flex flex-col items-center text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-1 tracking-tight leading-tight">
              CORAA
            </h2>
            <span className="text-sm text-white/80 mb-2">
              Your 24/7 AI Co-Pilot
            </span>
          </div>
          {/* Prompt Suggestions Strip */}
          <div className="w-full flex items-center justify-center px-0 pb-2">
            <div className="relative w-full px-0">
              <div className="overflow-hidden w-full">
                <div
                  className="flex gap-2 py-2 animate-prompt-scroll whitespace-nowrap"
                  style={{
                    animation: "prompt-scroll 130s linear infinite",
                    width: "fit-content",
                    minWidth: "100%",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.animationPlayState = "paused")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.animationPlayState = "running")
                  }
                >
                  {[...PROMPTS, ...PROMPTS].map((prompt, idx) => (
                    <button
                      key={idx}
                      className="flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-yellow-400 via-pink-500 to-red-500 text-white text-xs md:text-sm px-4 py-2 rounded-full font-medium hover:opacity-90 transition text-center leading-tight min-h-[36px]"
                      onClick={() => handleSendMessage(prompt)}
                      type="button"
                      tabIndex={-1}
                      style={{
                        whiteSpace: "normal",
                        textAlign: "center",
                        height: "36px",
                      }}
                    >
                      <span
                        className="block w-full"
                        style={{
                          lineHeight: "1.25",
                          fontSize: "1rem",
                          whiteSpace: "normal",
                        }}
                      >
                        {prompt}
                      </span>
                    </button>
                  ))}
                </div>
                <style>{`
                @keyframes prompt-scroll {
                  0% { transform: translateX(0%); }
                  100% { transform: translateX(-50%); }
                }
              `}</style>
              </div>
            </div>
          </div>

          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto px-10 py-4 space-y-4 bg-[#1a2030]">
            {chatMessages.length === 0 && !chatIsLoading && (
              <div className="text-center text-gray-400 my-8">
                <p className="text-base md:text-lg mb-4">
                  Ask me anything about your CSR & Volunteering needs!
                </p>
              </div>
            )}

            {/* Actual Chat Messages */}
            {chatMessages.map((msg) => {
              const hasHtml = containsHtml(msg.text);
              return (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`${
                      hasHtml ? "min-w-[50%] max-w-[85%]" : "max-w-[85%]"
                    } p-4 rounded-xl text-white shadow-md ${
                      msg.sender === "user"
                        ? "bg-blue-600 rounded-br-none"
                        : "bg-[#232b41] rounded-bl-none"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {msg.sender === "coraa" && (
                        <img
                          src="/images/coraa-mascot.png"
                          alt="CORAA Mascot"
                          className="h-8 w-8 rounded-full border-2 border-[#e0f2f1] bg-[#f8fafc] shadow object-cover mr-2"
                        />
                      )}
                      <span className="text-xs opacity-70">
                        {msg.sender === "user" ? "You" : "CORAA"} •{" "}
                        {format(msg.timestamp, "h:mm a")}
                      </span>
                    </div>
                    {renderMessageContent(msg.text)}
                  </div>
                </div>
              );
            })}

            {/* Loading Indicator */}
            {chatIsLoading && (
              <div className="flex justify-start">
                <div className="max-w-[80%] p-4 rounded-xl text-gray-300 bg-[#232b41] italic shadow-md rounded-bl-none">
                  CORAA is typing...
                </div>
              </div>
            )}

            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="px-10 py-6 border-t border-white/10">
            <div className="bg-[#232b41] rounded-2xl flex items-center px-6 py-4 shadow-inner">
              <input
                ref={inputRef}
                type="text"
                className="flex-1 bg-transparent text-white text-lg font-medium outline-none placeholder:text-white/20"
                placeholder="What's on your mind for Impact today?..."
                value={chatUserInput}
                onChange={(e) => setChatUserInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && !chatIsLoading && handleSendMessage()
                }
                disabled={chatIsLoading}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={chatIsLoading || !chatUserInput.trim()}
                className="ml-4 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white px-5 py-2 rounded-xl font-bold shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 12h14M12 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90">
          <div className="relative bg-transparent rounded-lg overflow-hidden max-w-[90vw] max-h-[90vh]">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-black/50 text-white hover:bg-black/70 flex items-center justify-center"
              aria-label="Close image"
            >
              <X className="h-4 w-4" />
            </button>
            <img
              src={selectedImage}
              alt="Full size image"
              className="max-h-[80vh] max-w-full object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CoraaAIDialog;
